import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTRPC } from "../lib/trpc.js";
import { Card, Empty, Spinner } from "./ui.js";
import { LineChart, SERIES_COLORS, type Series } from "./charts.js";

/**
 * The season as it actually unfolded, week by week.
 *
 * A standings table is a final position; it can't show that someone led for
 * six weeks and collapsed, or that two managers have never been more than a
 * game apart. Built from the schedule the page already has access to, so it
 * costs one extra query and no new endpoint.
 */
type Metric = "wins" | "diff";

export function SeasonRace({ seasonSlug }: { seasonSlug: string | undefined }) {
  const trpc = useTRPC();
  const [metric, setMetric] = useState<Metric>("wins");
  const [highlight, setHighlight] = useState<string | null>(null);
  const schedule = useQuery(trpc.league.schedule.queryOptions({ seasonSlug }));

  const { series, xLabels } = useMemo(() => {
    const weeks = (schedule.data?.weeks ?? []).filter((w) =>
      w.games.some((g) => g.status === "final"),
    );
    if (weeks.length === 0) return { series: [] as Series[], xLabels: [] as string[] };

    const running = new Map<string, { wins: number; diff: number }>();
    const history = new Map<string, { wins: number; diff: number }[]>();

    for (const w of weeks) {
      for (const g of w.games) {
        if (g.status !== "final" || g.home.score == null || g.away.score == null) continue;
        for (const [side, other] of [
          [g.home, g.away],
          [g.away, g.home],
        ] as const) {
          const name = side.managerName;
          if (!name) continue;
          const cur = running.get(name) ?? { wins: 0, diff: 0 };
          // A tie is half a win, which is how the standings score it too.
          const won = side.score! > other.score! ? 1 : side.score! === other.score! ? 0.5 : 0;
          running.set(name, {
            wins: cur.wins + won,
            diff: cur.diff + (side.score! - other.score!),
          });
        }
      }
      // Snapshot after each week, so a manager on a bye holds their line flat
      // rather than dropping out of the chart.
      for (const [name, v] of running) {
        if (!history.has(name)) history.set(name, []);
        history.get(name)!.push({ ...v });
      }
      for (const [name, h] of history) {
        while (h.length < weeks.indexOf(w) + 1) h.unshift({ wins: 0, diff: 0 });
      }
    }

    const out: Series[] = [...history.entries()]
      .sort((a, b) => {
        const av = a[1][a[1].length - 1];
        const bv = b[1][b[1].length - 1];
        return bv.wins - av.wins || bv.diff - av.diff;
      })
      .map(([label, points], i) => ({
        label,
        color: SERIES_COLORS[i % SERIES_COLORS.length],
        points: points.map((p) => (metric === "wins" ? p.wins : p.diff)),
      }));

    return {
      series: out,
      xLabels: weeks.map((w) => `W${w.number}`),
    };
  }, [schedule.data, metric]);

  return (
    <Card
      title="The race"
      action={
        <div className="flex items-center gap-2">
          <select
            className="input w-auto py-1 text-xs"
            value={highlight ?? ""}
            onChange={(e) => setHighlight(e.target.value || null)}
          >
            <option value="">Everyone</option>
            {series.map((s) => (
              <option key={s.label} value={s.label}>
                {s.label}
              </option>
            ))}
          </select>
          <select
            className="input w-auto py-1 text-xs"
            value={metric}
            onChange={(e) => setMetric(e.target.value as Metric)}
          >
            <option value="wins">Wins</option>
            <option value="diff">Point differential</option>
          </select>
        </div>
      }
    >
      {schedule.isLoading && <Spinner />}
      {!schedule.isLoading && series.length === 0 && (
        <Empty>No completed weeks yet.</Empty>
      )}
      {series.length > 0 && (
        <div className="px-2 py-3">
          <LineChart
            series={series}
            xLabels={xLabels}
            yLabel={metric === "wins" ? "Cumulative wins" : "Cumulative differential"}
            highlight={highlight}
            formatY={(v) => (metric === "wins" ? String(v) : v > 0 ? `+${v}` : String(v))}
          />
        </div>
      )}
      <p className="border-t border-field-800 px-4 py-3 text-xs text-chalk-500">
        Regular season only, cumulative after each week. Ties count as half a
        win, matching the standings. A flat stretch is a bye, not a loss.
      </p>
    </Card>
  );
}
